import ContactMailIcon from '@mui/icons-material/ContactMail';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import PostAddIcon from '@mui/icons-material/PostAdd';
import StartIcon from '@mui/icons-material/Start';
import StopIcon from '@mui/icons-material/Stop';
import { Box, Link, ListItemIcon, ListItemText, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { InviteApplicants } from 'Components/Applicants/InviteApplicants';
import { JobDescriptionDialog } from 'Components/JobsTs/JobDescriptionDialog';
import { JobSummaryStack } from 'Components/JobsTs/JobsListTs/Jobs.styled';
import { IJob, IJobSummary } from 'Components/JobsTs/JobsModel';
import { DistortionIcon, FitIcon, IncompleteIcon, StrongFitIcon, WeakFitIcon } from 'assets/Icons';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ISummaryComponentBase, RootState } from 'shared/SharedModels';
import { setBreadcrumbLabelRegistry, setBreadcrumbNavFrom } from 'store/slices/app/breadcrumb-slice';
import { getJobDescription } from 'store/slices/employer/jobs/jobs-list-slice';
import { JobType } from 'store/slices/employer/jobs/jobs-slice-model';


export const JobSummary = ({ summary, jobsType, job }: (ISummaryComponentBase<IJobSummary> & { jobsType: JobType, job: IJob })) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [isJobDescOpen, setIsJobDescOpen] = useState<boolean>(false);
    const [isInviteApplicantsOpen, setIsInviteApplicantsOpen] = useState<boolean>(false);
    const openJobDesc = () => {
        setIsJobDescOpen(true);
        if (!job.jobDetails) {
            dispatch(getJobDescription({ jobId: job.id }));
        }
    };
    const { accountAccess } = useAppSelector((state: RootState) => state.auth.auth);

    const gotoEditJob = () => {
        navigate(`/employer/job/${job.id}`);
        dispatch(setBreadcrumbNavFrom({ breadcrumbNav: `${jobsType}_jobs` }));
        dispatch(setBreadcrumbLabelRegistry({ labelReg: { [job.id]: `${job.name}` } }));
    };

    const gotoApplicants = () => {
        navigate(`/employer/applicants/${job.id}`)
        dispatch(setBreadcrumbNavFrom({ breadcrumbNav: `${jobsType}_jobs` }));
        dispatch(setBreadcrumbLabelRegistry({ labelReg: { [job.id]: `${job.name}` } }));
    };

    const gotoIncompleteApplicants = () => {
        dispatch(setBreadcrumbNavFrom({ breadcrumbNav: `${jobsType}_jobs` }));
        dispatch(setBreadcrumbLabelRegistry({ labelReg: { [job.id]: `${job.name}` } }));
    };

    return (<>
        <InviteApplicants isInviteApplicantsOpen={isInviteApplicantsOpen} job={job}
            setIsInviteApplicantsOpen={setIsInviteApplicantsOpen} />
        {/* {summary.matchTypeCounts?.find(mtc => mtc.count !== 0) &&
           } */}
        <JobSummaryStack>
            {jobsType !== 'draft' && <>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                    <Stack paddingX={{ xs: 5, sm: 2, md: 3, lg: 4, xl: 5 }}>
                        <Box className='job-detail'>
                            <Typography variant='subtitle2' className='label' whiteSpace='nowrap'>Total Applicants</Typography>
                            <Typography className='value'>
                                {(summary.matchTypeCounts?.map(mtc => mtc.count).reduce((total, cur) => total = total + cur) ?? 0) +
                                    (summary.inCompleteApplicantsCount ?? 0)}
                            </Typography>
                        </Box>
                        <Box className='job-detail'>
                            <StrongFitIcon />
                            <Typography variant='subtitle2' className='label'>Strong</Typography>
                            <Typography className='value'>
                                {summary.matchTypeCounts?.find(mtc => mtc.matchType === 'STRONG')?.count || 0}
                            </Typography>
                        </Box>
                        <Box className='job-detail'>
                            <FitIcon />
                            <Typography variant='subtitle2' className='label'>Fit</Typography>
                            <Typography className='value'>
                                {summary.matchTypeCounts?.find(mtc => mtc.matchType === 'MATCH')?.count || 0}
                            </Typography>
                        </Box>
                        <Box className='job-detail'>
                            <WeakFitIcon />
                            <Typography variant='subtitle2' className='label'>Weak</Typography>
                            <Typography className='value'>
                                {summary.matchTypeCounts?.find(mtc => mtc.matchType === 'NONE')?.count || 0}
                            </Typography>
                        </Box>
                        <Box className='job-detail toggle-in-dark'>
                            <DistortionIcon />
                            <Typography variant='subtitle2' className='label'>Distortion</Typography>
                            <Typography className='value'>
                                {summary.matchTypeCounts?.find(mtc => mtc.matchType === 'DISTORTION')?.count || 0}
                            </Typography>
                        </Box>
                        <Box className='job-detail toggle-in-dark'>
                            <IncompleteIcon />
                            <Typography variant='subtitle2' className='label'>Incomplete</Typography>
                            <Typography className='value'>
                                <Tooltip title="View incomplete applicants list" >
                                    <Link href={`/employer/incomplete-applicants/${job.id}`}
                                        onClick={gotoIncompleteApplicants}>
                                        {summary?.inCompleteApplicantsCount || 0}</Link>
                                </Tooltip>
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack paddingX={{ xs: 3, sm: 2, md: 3, lg: 4, xl: 5 }}>
                        <MenuItem onClick={gotoApplicants}>
                            <ListItemIcon><PeopleIcon color='secondary' /></ListItemIcon>
                            <ListItemText>View Applicants</ListItemText>
                        </MenuItem>
                        {jobsType !== 'active' && !(accountAccess?.role === 'hiring_manager' || accountAccess?.role === 'observer') &&
                            <MenuItem onClick={gotoEditJob}>
                                <ListItemIcon><StartIcon color='success' /></ListItemIcon>
                                <ListItemText>Start Hiring</ListItemText>
                            </MenuItem>}
                        {jobsType === 'active' &&
                            <MenuItem onClick={() => setIsInviteApplicantsOpen(true)}>
                                <ListItemIcon><ContactMailIcon color='success' /></ListItemIcon>
                                <ListItemText> Invite Applicants</ListItemText>
                            </MenuItem>}
                        {jobsType === 'active' && !(accountAccess?.role === 'hiring_manager' || accountAccess?.role === 'observer') &&
                            <MenuItem>
                                <ListItemIcon><StopIcon color='success' /></ListItemIcon>
                                <ListItemText> Stop Hiring</ListItemText>
                            </MenuItem>}
                        {!(accountAccess?.role === 'hiring_manager' || accountAccess?.role === 'observer') &&
                            <MenuItem onClick={gotoEditJob}>
                                <ListItemIcon><EditIcon color='primary' /></ListItemIcon>
                                <ListItemText>Edit Job</ListItemText>
                            </MenuItem>}
                        {jobsType === 'expired' &&
                            <MenuItem>
                                <ListItemIcon><PostAddIcon color='info' /></ListItemIcon>
                                <ListItemText>Re-post this Job</ListItemText>
                            </MenuItem>}
                        <MenuItem onClick={openJobDesc}>
                            <ListItemIcon><DescriptionIcon color='success' /></ListItemIcon>
                            <ListItemText> Job Description</ListItemText>
                        </MenuItem>
                    </Stack>
                </Stack>
            </>}
            {/* Job description dialog. */}
            <JobDescriptionDialog isJobDescOpen={isJobDescOpen} setIsJobDescOpen={setIsJobDescOpen}
                jobDescApiResponse={job.getJobDescResponse} jobDescApiStatus={job.getJobDescStatus}
                jobDescription={job.jobDetails?.description} jobName={job.jobDetails?.name} />
        </JobSummaryStack>
    </>);
};